import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { workshopDashboardService, PendingActionItem } from '@/app/services/dashboardService';
import { Clock, FileCheck, Inbox } from 'lucide-react';

interface ActionItemProps {
  item: PendingActionItem;
  onClick: () => void;
}

const ActionItem: React.FC<ActionItemProps> = ({ item, onClick }) => {
  const vehicleDisplay = [item.vehicle.make, item.vehicle.model].filter(Boolean).join(' ');

  return (
    <div
      className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors border-b last:border-b-0"
      onClick={onClick}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">
            {item.vehicle.registrationNumber}
          </span>
          {vehicleDisplay && (
            <span className="text-xs text-muted-foreground truncate">
              {vehicleDisplay}
            </span>
          )}
        </div>
        {item.party && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {item.party.name}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-2">
        {item.daysWaiting > 0 && (
          <Badge
            variant="outline"
            className={cn(
              'text-xs font-normal',
              item.daysWaiting >= 3
                ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400'
                : 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400'
            )}
          >
            <Clock className="h-3 w-3 mr-1" />
            {item.daysWaiting}d
          </Badge>
        )}
      </div>
    </div>
  );
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
    <Inbox className="h-8 w-8 mb-2 opacity-50" />
    <p className="text-sm">{message}</p>
  </div>
);

const SectionSkeleton: React.FC = () => (
  <div className="space-y-2">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-center justify-between p-3">
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-32" />
        </div>
        <Skeleton className="h-5 w-12" />
      </div>
    ))}
  </div>
);

const PendingActionsPanel: React.FC = () => {
  const navigate = useNavigate();

  const { data: pendingActions, isLoading } = useQuery({
    queryKey: ['workshopDashboard', 'pendingActions'],
    queryFn: () => workshopDashboardService.getPendingActions(),
    refetchInterval: 60000, // Refresh every 60 seconds
    staleTime: 30000,
  });

  const handleItemClick = (id: number) => {
    navigate(`/workshop/job-cards/detail/${id}`);
  };

  const awaitingApproval = pendingActions?.awaitingApproval || [];
  const qualityCheckNeeded = pendingActions?.qualityCheckNeeded || [];
  const totalPending = awaitingApproval.length + qualityCheckNeeded.length;

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <FileCheck className="h-4 w-4 text-slate-500" />
            Pending Actions
          </CardTitle>
          {totalPending > 0 && (
            <Badge variant="secondary" className="font-medium">
              {totalPending}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <SectionSkeleton />
        ) : totalPending === 0 ? (
          <EmptyState message="No pending actions" />
        ) : (
          <ScrollArea className="h-[280px]">
            {/* Awaiting Approval Section */}
            {awaitingApproval.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Awaiting Approval
                  </span>
                  <Badge variant="outline" className="h-5 text-xs font-normal bg-slate-50 text-slate-600 border-slate-200">
                    {awaitingApproval.length}
                  </Badge>
                </div>
                <div className="border rounded-lg">
                  {awaitingApproval.slice(0, 5).map((item) => (
                    <ActionItem
                      key={item.id}
                      item={item}
                      onClick={() => handleItemClick(item.id)}
                    />
                  ))}
                </div>
                {awaitingApproval.length > 5 && (
                  <button
                    className="text-xs text-primary hover:underline mt-2 px-1"
                    onClick={() => {
                      const filters = encodeURIComponent(JSON.stringify({ status: 'quoted' }));
                      navigate(`/workshop/job-cards/list?filters=${filters}`);
                    }}
                  >
                    View all {awaitingApproval.length} awaiting approval
                  </button>
                )}
              </div>
            )}

            {/* Quality Check Section */}
            {qualityCheckNeeded.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Quality Check Needed
                  </span>
                  <Badge variant="outline" className="h-5 text-xs font-normal bg-slate-50 text-slate-600 border-slate-200">
                    {qualityCheckNeeded.length}
                  </Badge>
                </div>
                <div className="border rounded-lg">
                  {qualityCheckNeeded.slice(0, 5).map((item) => (
                    <ActionItem
                      key={item.id}
                      item={item}
                      onClick={() => handleItemClick(item.id)}
                    />
                  ))}
                </div>
                {qualityCheckNeeded.length > 5 && (
                  <button
                    className="text-xs text-primary hover:underline mt-2 px-1"
                    onClick={() => {
                      const filters = encodeURIComponent(JSON.stringify({ status: 'quality_check' }));
                      navigate(`/workshop/job-cards/list?filters=${filters}`);
                    }}
                  >
                    View all {qualityCheckNeeded.length} needing QC
                  </button>
                )}
              </div>
            )}
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default PendingActionsPanel;
